import { useEffect, useState } from "react";
import BaseWrapper from "../../components/ui/BaseWrapper";
import dayjs from "dayjs";
import FilterChip from "../../components/FilterChip";
import { useNavigate, useSearchParams } from "react-router-dom";
import BaseButton from "../../components/ui/BaseButton";
import { Menu, MenuItem } from "@mui/material";
import BaseRating from "../../components/ui/BaseRating";
import WorkOrderStatus from "../../components/WorkOrderStatus";
import { getTimeZoneAbbreviation } from "../../utils/getTimeZoneAbbreviation";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import { CurrencySymbol, PaymentType } from "../../utils/enum";
import useAuth from "../../hooks/useAuth";
import { useSelector } from "react-redux";
import { useCurrentUser } from "../../hooks/useCurrentUser";
const relativeTime = require("dayjs/plugin/relativeTime");

dayjs.extend(relativeTime);

const FindJobTable = ({
  jobs,
  setJobsFilters,
  jobsCount,
  jobsFilters,
  paginationModel,
  onPaginationModelChange,
  rowCount,
  search,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const navigate = useNavigate();
  const { userData } = useCurrentUser();

  const isCompanyMember = !!userData?.isCompanyMember;
  const defaultFilter = searchParams.get("filter") || (isCompanyMember ? "ASSIGNED" : "AVAILABLE");

  useEffect(() => {
    if (!userData) return;
    const resolvedDefault = isCompanyMember
      ? searchParams.get("filter") || "ASSIGNED"
      : searchParams.get("filter") || "AVAILABLE";
    setJobsFilters({
      status:
        resolvedDefault === "COUNTER"
          ? "PENDING"
          : resolvedDefault === "REQUESTED"
            ? "PENDING"
            : resolvedDefault === "ALL"
              ? ""
              : resolvedDefault,
      isCounter: resolvedDefault === "COUNTER" ? true : false,
    });
  }, [isCompanyMember]);

  const handleNavigate = (id) => {
    navigate(`/workOrder/details/${id}`);
  };

  const handleFilterClick = (filter, isCounter) => {
    const updatedActiveFilter =
      filter === "PENDING" && isCounter === true
        ? "COUNTER"
        : filter === "PENDING" && isCounter === false
          ? "REQUESTED"
          : filter;

    const updatedApiFilter = filter === "ALL" ? "" : filter;

    setJobsFilters({
      status: updatedApiFilter === "ALL" ? "" : updatedApiFilter,
      ...(isCounter !== undefined && { isCounter }), // Conditionally add isCounter
    });
    setSearchParams({
      filter: updatedActiveFilter,
    });
  };

  const handleContextMenu = (event, item) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
    setSelectedWorkOrder(item);
  };

  const handleClose = () => {
    setContextMenu(null);
    setSelectedWorkOrder(null);
  };
  const getPayValue = (params, field) => {
    const engineer = params.row?.engineer;
    const offer = params.row?.offers;
    const row = params.row;

    const engineerFieldMap = {
      hourlyRate: engineer?.offer?.hourlyRate,
      fullDayRate: engineer?.offer?.fullDayRate,
      halfDayRate: engineer?.offer?.halfDayRate,
      fthRate: engineer?.offer?.fthRate,
      fixedAmount: engineer?.offer?.fixedAmount,
      minimumHours: engineer?.offer?.minimumHours,
    };
    const offerFieldMap = {
      hourlyRate: offer?.hourlyRate,
      fullDayRate: offer?.fullDayRate,
      halfDayRate: offer?.halfDayRate,
      fixedAmount: offer?.fixedAmount,
      minimumHours: offer?.minimumHours,
    };

    const rowFieldMap = {
      hourlyRate: row?.engineerHourlyRate,
      fullDayRate: row?.engineerFullDayRate,
      halfDayRate: row?.engineerHalfDayRate,
      fixedAmount: row?.engineerFixedAmount,
      minimumHours: row?.engineerMinimumHours,
    };

    const userBillingRatesMap = {};
    if (userData?.billingRates?.length) {
      userData.billingRates.forEach((rate) => {
        switch (rate.paymentType) {
          case "HOURLY":
            userBillingRatesMap.hourlyRate = rate.rate;
            break;
          case "HALF_DAY":
            userBillingRatesMap.halfDayRate = rate.rate;
            break;
          case "FULL_DAY":
            userBillingRatesMap.fullDayRate = rate.rate;
            break;
          case "FIXED":
            userBillingRatesMap.fixedAmount = rate.rate;
            break;
        }
      });
    }

    return (
      engineerFieldMap[field] ??
      offerFieldMap[field] ??
      rowFieldMap[field] ??
      userBillingRatesMap[field] ??
      null
    );
  };

  const data = jobs || [];

  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      width: 100,
      renderCell: (params) => (
        <div className="flex items-center w-[206px]">
          <p
            className="table-font-size font-normal text-primary cursor-pointer ml-4"
            onClick={() => handleNavigate(params.value)}
            // onContextMenu={(e) => handleContextMenu(e, params.row)}
          >
            {params.row?.referenceId}
          </p>
        </div>
      ),
    },
    {
      field: "incidentId",
      headerName: "Incident ID",
      width: 120,
      renderCell: (params) => (
        <p className="table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap">
          {params.row?.incidentId}
        </p>
      ),
    },
    {
      field: "title",
      headerName: "Job Title",
      width: 312,
      renderCell: (params) => (
        <p
          className="table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap text-primary cursor-pointer"
          onClick={() => handleNavigate(params.row.id)}
          onContextMenu={(e) => handleContextMenu(e, params.row)}
        >
          {params.row?.title}
        </p>
      ),
    },
    {
      field: "address",
      headerName: "Location",
      width: 220,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">
          {`${params.row?.address?.addressLine1}, ${params.row?.address?.country}`}
        </p>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 180,
      renderCell: (params) => {
        const item = params.row;
        const effectiveStatus =
          item?.status === "PUBLISHED" ||
          jobsFilters?.status === "AVAILABLE" ||
          jobsFilters?.status === "ASSIGNED" ||
          jobsFilters?.status === "INPROGRESS" ||
          jobsFilters?.status === "WORKDONE" ||
          jobsFilters?.status === "INCOMPLETE" ||
          jobsFilters?.status === "VERIFIED" ||
          jobsFilters?.status === "APPROVED" ||
          jobsFilters?.status === "ROUTED" ||
          jobsFilters?.status === "COUNTER" ||
          jobsFilters?.status === "REQUESTED" ||
          jobsFilters?.status === ""
            ? item?.offers?.status === "DECLINED"
              ? item?.offers?.status
              : item?.offers?.status === "ROUTED"
                ? item?.offers?.status
                : item?.offers?.status === "ACCEPTED"
                  ? "ROUTED"
                  : item?.offers?.status === "PENDING" && item?.offers?.isCounter === true
                    ? "COUNTER"
                    : item?.offers?.status === "PENDING"
                      ? "REQUESTED"
                      : item?.status === "VERIFIED"
                        ? "WORKDONE"
                        : item?.status
            : item?.status;

        return (
          <div className="flex items-center">
            <WorkOrderStatus
              className="!py-[1.8px] !w-[70px] !text-[11px]"
              status={effectiveStatus}
            />
            {item?.status !== "CANCELLED" &&
              effectiveStatus !== "COUNTER" &&
              item?.subStatus &&
              !["PO_PENDING", "REVIEWED"].includes(item.subStatus) && (
                <>
                  <i className="fa-regular fa-arrow-right-long mx-2 text-primary"></i>
                  <div className="ml-1">
                    <WorkOrderStatus
                      status={item.subStatus}
                      className="!py-[1.5px] !w-[80px] !text-[9px]"
                    />
                  </div>
                </>
              )}
          </div>
        );
      },
    },
    {
      field: "category",
      headerName: "Type of Work",
      width: 160,
      renderCell: (params) => (
        <p className="table-font-size">{params.row?.category?.category || "not specified"}</p>
      ),
    },
    {
      field: "schedule",
      headerName: "Schedule",
      width: 220,
      renderCell: (params) => {
        const item = params.row;
        return (
          <div>
            <p className="table-font-size">
              {dayjs(item?.orderStartDate).format("MM/DD/YYYY")} at{" "}
              {dayjs.utc(item?.orderStartTime).format("hh:mm A")}
              <span> ({getTimeZoneAbbreviation(item?.timeZone)})</span>
            </p>
          </div>
        );
      },
    },
    {
      field: "payment",
      headerName: "Pay",
      width: 220,
      renderCell: (params) => {
        const item = params.row;

        return (
          <div>
            {item?.engineerPaymentType === "HOURLY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.HOURLY}</span> @{" "}
                {CurrencySymbol[params.row?.engineerCurrency] || "USD"}{" "}
                {getPayValue(params, "hourlyRate")?.toFixed(2)} (min{" "}
                {getPayValue(params, "minimumHours")} hrs)
              </p>
            )}
            {item?.engineerPaymentType === "HALF_DAY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.HALF_DAY}</span> @{" "}
                {CurrencySymbol[params.row?.engineerCurrency] || "USD"}{" "}
                {getPayValue(params, "halfDayRate")?.toFixed(2)} & Additional hrs @{" "}
                {CurrencySymbol[params.row?.engineerCurrency] || "USD"}{" "}
                {getPayValue(params, "hourlyRate")?.toFixed(2)}
              </p>
            )}
            {item?.engineerPaymentType === "FULL_DAY" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FULL_DAY}</span> @{" "}
                {CurrencySymbol[params.row?.engineerCurrency] || "USD"}{" "}
                {getPayValue(params, "fullDayRate")?.toFixed(2)}
              </p>
            )}
            {item?.engineerPaymentType === "FIXED" && (
              <p className="table-font-size">
                <span className="text-primary">{PaymentType.FIXED}</span> @{" "}
                {CurrencySymbol[params.row?.engineerCurrency] || "USD"}{" "}
                {getPayValue(params, "fixedAmount")?.toFixed(2)}
              </p>
            )}
          </div>
        );
      },
    },
    {
      field: "distance",
      headerName: "Distance",
      width: 140,
      renderCell: (params) => (
        <div className="flex items-center gap-2">
          <i className="fa-light fa-car text-gray-500 text-base"></i>
          <p className="table-font-size text-primary">{params.row?.distance?.toFixed(2)} km</p>
        </div>
      ),
    },
    {
      field: "company",
      headerName: "Company",
      width: 180,
      renderCell: (params) => (
        <p className="table-font-size">{params.row?.createdBy?.company?.name || ""}</p>
      ),
    },
    {
      field: "buyerRating",
      headerName: "Buyer Rating",
      width: 160,
      renderCell: () => (
        <div className="flex items-center justify-center border border-primary w-20 py-1 rounded-full">
          <BaseRating style={{ fontSize: "13px" }} />
        </div>
      ),
    },
    {
      field: "totalOffers",
      headerName: "Requests",
      width: 120,
      renderCell: (params) => <p className="table-font-size">{params.row?.totalOffers || 0}</p>,
    },
  ].filter((column) => !(isCompanyMember && column.field === "payment"));

  return (
    <BaseWrapper>
      <div
        className=" my-4
            flex
            overflow-x-auto
            scrollbar-thin
            scrollbar-thumb-gray-400
            scrollbar-track-gray-200
            [&::-webkit-scrollbar]:h-2
            [&::-webkit-scrollbar-thumb]:rounded
            [&::-webkit-scrollbar-thumb]:bg-gray-400
            [&::-webkit-scrollbar-track]:bg-gray-200
          "
      >
        {!isCompanyMember && (
          <div className="mr-5">
            <FilterChip
              filter="Available"
              count={
                search && jobsFilters?.status === "AVAILABLE" ? rowCount : jobsCount?.AVAILABLE || 0
              }
              active={jobsFilters?.status === "AVAILABLE" ? true : false}
              onClick={() => handleFilterClick("AVAILABLE")}
            />
          </div>
        )}
        {!isCompanyMember && (
          <div className="mr-5">
            <FilterChip
              filter="Routed"
              count={search && jobsFilters?.status === "ROUTED" ? rowCount : jobsCount?.ROUTED || 0}
              active={jobsFilters?.status === "ROUTED" ? true : false}
              onClick={() => {
                handleFilterClick("ROUTED");
              }}
            />
          </div>
        )}
        {!isCompanyMember && (
          <div className="mr-5">
            <FilterChip
              filter="Countered"
              count={
                search && jobsFilters?.status === "PENDING" && jobsFilters?.isCounter === true
                  ? rowCount
                  : jobsCount?.COUNTERED || 0
              }
              active={
                jobsFilters?.status === "PENDING" && jobsFilters?.isCounter === true ? true : false
              }
              onClick={() => {
                handleFilterClick("PENDING", true);
              }}
            />
          </div>
        )}
        {!isCompanyMember && (
          <div className="mr-5">
            <FilterChip
              filter="Requested"
              count={
                search && jobsFilters?.status === "PENDING" && jobsFilters?.isCounter === false
                  ? rowCount
                  : jobsCount?.REQUESTED || 0
              }
              active={
                jobsFilters?.status === "PENDING" && jobsFilters?.isCounter === false ? true : false
              }
              onClick={() => {
                handleFilterClick("PENDING", false);
              }}
            />
          </div>
        )}

        <div className="mr-5">
          <FilterChip
            filter="Assigned"
            count={
              search && jobsFilters?.status === "ASSIGNED" ? rowCount : jobsCount?.ASSIGNED || 0
            }
            active={jobsFilters?.status === "ASSIGNED" ? true : false}
            onClick={() => {
              handleFilterClick("ASSIGNED");
            }}
          />
        </div>
        <div className="mr-5">
          <FilterChip
            filter="Inprogress"
            count={
              search && jobsFilters?.status === "INPROGRESS" ? rowCount : jobsCount?.INPROGRESS || 0
            }
            active={jobsFilters?.status === "INPROGRESS" ? true : false}
            onClick={() => {
              handleFilterClick("INPROGRESS");
            }}
          />
        </div>
        <div className="mr-5">
          <FilterChip
            filter="Work Done"
            count={
              search && (jobsFilters?.status === "WORKDONE" || jobsFilters?.status === "VERIFIED")
                ? rowCount
                : jobsCount?.WORKDONE || 0
            }
            active={jobsFilters?.status === "WORKDONE" ? true : false}
            onClick={() => {
              handleFilterClick("WORKDONE");
            }}
          />
        </div>
        <div className="mr-5">
          <FilterChip
            filter="Incomplete"
            count={
              search && jobsFilters?.status === "INCOMPLETE" ? rowCount : jobsCount?.INCOMPLETE || 0
            }
            active={jobsFilters?.status === "INCOMPLETE" ? true : false}
            onClick={() => {
              handleFilterClick("INCOMPLETE");
            }}
          />
        </div>
        <div className="mr-5">
          <FilterChip
            filter="Approved"
            count={
              search && jobsFilters?.status === "APPROVED" ? rowCount : jobsCount?.APPROVED || 0
            }
            active={jobsFilters?.status === "APPROVED" ? true : false}
            onClick={() => {
              handleFilterClick("APPROVED");
            }}
          />
        </div>

        {!isCompanyMember && (
          <div className="mr-5">
            <FilterChip
              filter="Paid"
              count={search && jobsFilters?.status === "PAID" ? rowCount : jobsCount?.PAID || 0}
              active={jobsFilters?.status === "PAID" ? true : false}
              onClick={() => {
                handleFilterClick("PAID");
              }}
            />
          </div>
        )}

        <div className="mr-5">
          <FilterChip
            filter="All"
            count={search && jobsFilters?.status === "" ? rowCount : jobsCount?.ALL || 0}
            active={jobsFilters?.status === "" ? true : false}
            onClick={() => {
              handleFilterClick("ALL");
            }}
          />
        </div>
      </div>

      <div className="flex mb-4 mt-3">
        {jobsFilters?.status === "ROUTED" && (
          <>
            <BaseButton
              variant="outlined"
              color="primary"
              className="!p-0 h-8 w-28 mr-4"
              fullWidth={false}
            >
              Declined
            </BaseButton>
            <BaseButton
              variant="outlined"
              color="primary"
              className="!p-0 h-8 w-28 mr-4"
              fullWidth={false}
            >
              Accept
            </BaseButton>
          </>
        )}
        {(jobsFilters?.status === "COUNTER" || jobsFilters?.status === "REQUESTED") && (
          <BaseButton
            variant="outlined"
            color="primary"
            className="!p-0 h-8 w-28 mr-4"
            fullWidth={false}
          >
            Withdraw
          </BaseButton>
        )}

        <BaseButton
          variant="outlined"
          color="primary"
          className="!p-0 h-8 w-28 mr-4"
          fullWidth={false}
        >
          Export
        </BaseButton>

        {jobsFilters?.status === "ASSIGNED" && (
          <BaseButton
            variant="outlined"
            color="primary"
            className="!p-0 h-8 w-36"
            fullWidth={false}
          >
            Cancel Assignment
          </BaseButton>
        )}
      </div>
      <BaseDataTableV2
        className="max-h-[calc(100vh-300px)]"
        rowHeight={38}
        columns={columns}
        data={data || []}
        isLoading={false}
        disableColumnResize={false}
        checkboxSelection
        hideFooter={false}
        pagination
        rowCount={rowCount}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        paginationMode="server"
      />
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
        }
      >
        <MenuItem
          onClick={() => {
            handleNavigate(selectedWorkOrder.id);
            handleClose();
          }}
        >
          <p>Open</p>
        </MenuItem>
        <MenuItem
          onClick={() => {
            window.open(`/workOrder/details/${selectedWorkOrder.id}`, "_blank");
            handleClose();
          }}
        >
          <p>Open in New Tab</p>
        </MenuItem>
      </Menu>
    </BaseWrapper>
  );
};

export default FindJobTable;
